import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
}

const ServicesSection = () => {
  const services: Service[] = [
    {
      id: 1,
      icon: 'HomeIcon',
      title: 'Patio Cleaning',
      description: 'Professional deep cleaning for all patio surfaces, removing desert dust, stains, and buildup.',
      features: ['Pressure washing', 'Stain removal', 'Sealing available']
    },
    {
      id: 2,
      icon: 'SparklesIcon',
      title: 'Paver Restoration',
      description: 'Restore your pavers to like-new condition with our specialized cleaning and sealing process.',
      features: ['Deep cleaning', 'Re-sanding', 'Protective sealing']
    },
    {
      id: 3,
      icon: 'SunIcon',
      title: 'Pool Deck Cleaning',
      description: 'Safe, effective cleaning for pool decks that removes algae, calcium deposits, and stains.',
      features: ['Non-slip treatment', 'Algae removal', 'UV protection']
    },
    {
      id: 4,
      icon: 'BuildingOfficeIcon',
      title: 'Commercial Services',
      description: 'Large-scale cleaning solutions for commercial properties, HOAs, and property management.',
      features: ['Scheduled maintenance', 'Volume pricing', 'Minimal disruption']
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Our Patio Cleaning Services
            </h2>
            <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive patio cleaning solutions designed specifically for Phoenix's unique desert climate challenges
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-card rounded-lg p-6 shadow-subtle hover:shadow-brand-elevated transition-brand border border-border"
              >
                <div className="w-14 h-14 bg-desert-gradient rounded-lg flex items-center justify-center mb-4 shadow-brand">
                  <Icon name={service.icon as any} size={28} className="text-white" />
                </div>
                <h3 className="font-headline font-semibold text-xl text-card-foreground mb-3">
                  {service.title}
                </h3>
                <p className="font-body text-muted-foreground mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm font-body text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={16} className="text-success mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/services"
                  className="inline-flex items-center text-primary font-body font-medium hover:text-primary/80 transition-brand"
                >
                  Learn More
                  <Icon name="ArrowRightIcon" size={16} className="ml-2" />
                </Link>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link
              href="/services"
              className="inline-block px-8 py-4 bg-primary text-primary-foreground rounded-lg font-body font-semibold text-lg hover:bg-primary/90 transition-brand shadow-brand"
            >
              View All Services
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;